import React from 'react';
import {useNavigate} from 'react-router-dom';
import {DialogType} from 'components/FluentUICompat';
import {BaseDialog} from 'components/Dialogs/BaseDialog';
import {ConfirmUpdateDialog, useDialogState} from 'components/Dialogs';
import {useCommand} from 'hooks/useCommand';
import {useParagraphsContext} from 'features/RegulatoryDocuments/components/DocumentDetails/ParagraphsContext';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {
	useAssignRequirementParagraphsMutation,
	useRemoveRequirementAssignedParagraphsMutation,
} from '../hooks/requirements.generated';
import {RequirementDetailsDocument} from '../hooks/requirementDetails.generated';
import {Requirement, RequirementParagraphRefInput} from 'types';
import {useUserContext} from 'authentication/UserContext';
import {RequirementFromDetailsPage} from './requirementDetailsPage.types';
import {RequirementUtilsService} from 'features/Requirements/RequirementUtils.service';
import {useCloneRequirement} from 'features/Requirements/hooks/useCloneRequirement';

interface DeleteSegmentCommandProps {
	requirement: RequirementFromDetailsPage;
}

export const DeleteSegmentCommand: React.FC<DeleteSegmentCommandProps> = ({
	requirement,
}) => {
	const requirementId = requirement.id;
	const {t} = useRequirementsTranslation();
	const {selectedParagraphs, setSelectedParagraphs} = useParagraphsContext();
	const [dialogState, showDialog, hideDialog] = useDialogState();
	const [
		finalConfirmDialogState,
		showFinalConfirmDialog,
		hideFinalConfirmDialog,
	] = useDialogState();
	const {isAdmin, isVex, isVexSupport} = useUserContext();
	const {isRequirementFinalEntity} = new RequirementUtilsService();
	const {cloneRequirement} = useCloneRequirement();
	const navigate = useNavigate();

	const [removeAssignedParagraphs] =
		useRemoveRequirementAssignedParagraphsMutation({
			refetchQueries: [RequirementDetailsDocument],
		});
	const [assignParagraphs] = useAssignRequirementParagraphsMutation({
		refetchQueries: [RequirementDetailsDocument],
	});

	// Holds the paragraph IDs to remove, captured at click-time so values
	// survive dialog focus changes that may clear the context selection.
	const pendingRemoveRef = React.useRef<Set<string> | null>(null);

	const buildChanges = (): RequirementParagraphRefInput[] =>
		Object.values(
			selectedParagraphs.reduce<Record<string, RequirementParagraphRefInput>>(
				(acc, paragraph) => {
					const {regulation} = paragraph.parent;
					if (!regulation) return acc;

					const regdocId = paragraph.parent.id;
					if (!acc[regdocId]) {
						acc[regdocId] = {
							regulationRef: {id: regulation.id},
							regdocRef: {id: regdocId},
							paragraphRefs: [],
						};
					}
					acc[regdocId].paragraphRefs.push({id: paragraph.id});
					return acc;
				},
				{},
			),
		);

	const handleConfirm = React.useCallback(async () => {
		hideDialog();
		const changes = buildChanges();
		if (changes.length === 0) return;

		if (isRequirementFinalEntity(requirement)) {
			pendingRemoveRef.current = new Set(
				changes.flatMap(c => c.paragraphRefs.map(p => p.id)),
			);
			showFinalConfirmDialog();
			return;
		}
		await removeAssignedParagraphs({
			variables: {requirementId, changes},
		});
		setSelectedParagraphs([]);
	}, [
		hideDialog,
		buildChanges,
		removeAssignedParagraphs,
		setSelectedParagraphs,
		isRequirementFinalEntity,
		requirement,
		showFinalConfirmDialog,
		requirementId,
	]);

	const onFinalConfirmed = React.useCallback(async () => {
		hideFinalConfirmDialog();
		const removedIds = pendingRemoveRef.current;
		pendingRemoveRef.current = null;
		if (!removedIds || removedIds.size === 0) return;
		try {
			// Clone the requirement metadata only. Since the backend reassign
			// step cannot copy segments (paragraph-side back-ref is empty), we
			// build the surviving segment list from the source's own
			// assignedParagraphs and write it to the clone directly.
			const cloneRes = await cloneRequirement(
				requirement as unknown as Requirement,
				[RequirementDetailsDocument],
				undefined,
			);
			const cloneId = cloneRes?.data?.createRequirement?.requirement?.id;
			if (!cloneId) return;

			const byRegdoc: Record<string, RequirementParagraphRefInput> = {};
			for (const p of requirement.assignedParagraphs ?? []) {
				if (removedIds.has(p.id)) continue;
				const regulation = p.parent?.regulation;
				const regdocId = p.parent?.id;
				if (!regulation || !regdocId) continue;
				if (!byRegdoc[regdocId]) {
					byRegdoc[regdocId] = {
						regulationRef: {id: regulation.id},
						regdocRef: {id: regdocId},
						paragraphRefs: [],
					};
				}
				byRegdoc[regdocId].paragraphRefs.push({id: p.id});
			}
			const requirementParagraphRefsChange = Object.values(byRegdoc);

			if (requirementParagraphRefsChange.length > 0) {
				await assignParagraphs({
					variables: {
						input: {
							requirementId: cloneId,
							requirementParagraphRefsChange,
						},
					},
				});
			}

			setSelectedParagraphs([]);
			navigate(`/requirements/${cloneId}`);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('[DeleteSegmentCommand] onFinalConfirmed failed', err);
		}
	}, [
		hideFinalConfirmDialog,
		cloneRequirement,
		requirement,
		assignParagraphs,
		setSelectedParagraphs,
		navigate,
	]);

	useCommand(
		{
			key: 'deleteSegment',
			priority: 3,
			text: t('DeleteSegment'),
			iconProps: {iconName: 'Delete'},
			disabled: selectedParagraphs.length === 0,
			onClick: showDialog,
			hidden: !isAdmin && !isVex && !isVexSupport,
		},
		[selectedParagraphs],
	);

	return (
		<>
			<BaseDialog
				{...dialogState}
				onConfirm={handleConfirm}
				confirmText={t('DeleteSegmentDialog.Confirm')}
				cancelText={t('DeleteSegmentDialog.Cancel')}
				dialogContentProps={{
					type: DialogType.normal,
					title: t('DeleteSegmentDialog.Title'),
					subText: t('DeleteSegmentDialog.Subtext'),
				}}
			/>
			<ConfirmUpdateDialog
				{...finalConfirmDialogState}
				translationKeyPrefix="ConfirmFinalRequirementUpdateDialog"
				onConfirm={onFinalConfirmed}
			/>
		</>
	);
};
